import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, ComponentFactoryResolver, DoCheck,
  OnDestroy,
  OnInit,
  TemplateRef, ViewChild,
} from '@angular/core';

import {Subscription, Subject, fromEvent} from "rxjs";
import {ActivatedRoute, ParamMap, Router, UrlSegment} from "@angular/router";
import {CatalogBreadcrumbModel} from "./catalog-breadcrumb.model";
import {CatalogNavigationService} from "../../services/catalog-navigation/catalog-navigation.service";
import {UserService} from "../../../../core/services/user/user.service";
import { debounceTime, map } from 'rxjs/operators';
import {CatalogDataModel} from "../../../../core/services/catalog-data/catalog-data.model";
import {CatalogDataService} from "../../../../core/services/catalog-data/catalog-data.service";
import {$translateKey} from "../../../../core/services/internationalization/localize.service";
import {AlertService} from "../../../../core/services/alert/alert.service";
import {CatalogRequestCreatedEvent} from "../catalog-form/catalog-request-created-event.model";

enum CatalogLocation {
  Init,
  Root,
  Category,
  Item,
  Success,
  Search
}

@Component({
  selector: 'sb-catalog',
  templateUrl: './catalog.component.html',
  styleUrls: ['./catalog.component.scss']
})
export class CatalogComponent implements OnInit, OnDestroy, AfterViewInit {

  CatalogLocation = CatalogLocation; // Needed because otherwise the template cannot see it.

  @ViewChild('catalogItemForm') public catalogItemForm: TemplateRef<any>;

  location: CatalogLocation = CatalogLocation.Init;

  searchText: string;
  searchResults: CatalogDataModel[] = [];
  searchBreadcrumb: CatalogBreadcrumbModel[] = [];
  searchCatalogItemId: string;

  root: CatalogDataModel;
  previousCategory: CatalogDataModel;
  currentCategory: CatalogDataModel;
  currentItem: CatalogDataModel;

  itemSubmitted = false;
  lastRequestId: string;
  private lastRequestLink: string = null;

  navigatedFromPortal = false;

  numItems: number = 0;
  numItemsNextLevel: number = 0;
  breadcrumb: CatalogBreadcrumbModel[] = [];

  categories: CatalogDataModel[] = [];
  items: CatalogDataModel[] = [];
  subtreeItems: CatalogDataModel[] = [];
  subtreeItemsNextLevel: CatalogDataModel[] = [];

  private navigateToItemSubscription: Subscription | null;
  private navigateToCategorySubscription: Subscription | null;
  private navigateSubscription: Subscription | null;
  private catalogDataSubscription: Subscription | null;
  private debounceSubscription: Subscription | null;

  // Set up subscriptions that will allow us to respond to the initial navigation if requested
  private startupInitialNavigationSubscription: Subscription | null;
  private startupIsInitialNavigationInitialised: boolean = false;
  private startupInitialNavigationID: string = null;

  isChildWindowDisplay: boolean = true;

  private categoryIdToModel: {
    [key: string]: CatalogDataModel
  } = {};

  private itemIdToModel: {
    [key: string]: CatalogDataModel
  } = {};

  constructor(private activatedRoute: ActivatedRoute,
              // private currentLocation: Location,
              private router: Router,
              private catalogDataService: CatalogDataService,
              private catalogNavigationService: CatalogNavigationService,
              private userService: UserService,
              private alertService: AlertService) {
  }

  ngOnInit() {
    this.location = CatalogLocation.Init;

    const that = this;

    this.root = this.catalogDataService.getCatalog();

    this.isChildWindowDisplay = this.getIsChildWindow();

    if (this.root) {
      this.populateLookups();

      this.searchBreadcrumb = [{type: this.root.type, id: this.root.id, name: this.root.name}];

    } else {
      this.catalogDataSubscription = this.catalogDataService.catalogLoaded.subscribe((_) => {
        that.root = that.catalogDataService.getCatalog();
        that.populateLookups();

        // Check to see if we have an initial ID that we need to navigate to once the catalog data has loaded
        if (this.startupInitialNavigationID != null && this.startupIsInitialNavigationInitialised == false) {
          this.startupIsInitialNavigationInitialised = true;
          if (this.startupInitialNavigationID != null) {
            that.navigateTo(this.startupInitialNavigationID);
          }
        } else {
          that.navigateToCategory(that.root.id);
        }
      })
    }

    // This navigation is from a highlighted item or from a category page.
    if (!this.navigateToItemSubscription) {
      this.navigateToItemSubscription = this.catalogNavigationService.navigateToItem.subscribe((itemId) => {
        that.previousCategory = that.currentCategory;
        that.navigatedFromPortal = false;
        that.navigateToItem(itemId);
      });
    }

    if (!this.navigateToCategorySubscription) {
      this.navigateToCategorySubscription = this.catalogNavigationService.navigateToCategory.subscribe((itemId) => {
        that.previousCategory = that.currentCategory;
        that.navigatedFromPortal = false;
        that.navigateToCategory(itemId);
      });
    }

    // Subscription for tracking the startup parameters
    if (!this.startupInitialNavigationSubscription) {
      this.startupInitialNavigationSubscription = this.activatedRoute.paramMap.subscribe( (value: ParamMap) => {
        // Only do this on startup
        if (this.startupIsInitialNavigationInitialised == false && this.startupInitialNavigationID == null) {
          if (value.get("id")) {
            this.startupInitialNavigationID = value.get("id");
          }
        }
      });
    }

    // This navigation is from the portal.
    if (!this.navigateSubscription) {
      this.navigateSubscription = this.activatedRoute.paramMap.subscribe((value: ParamMap) => {
        this.navigatedFromPortal = true;
        if (!that.root) {
          that.root = that.catalogDataService.getCatalog();
        }
        if (that.root) {
          that.populateLookups();
          const id = value.get("id");
          if (id) {
            if (that.categoryIdToModel[id]) {
              that.navigateToCategory(id);
            } else if (that.itemIdToModel[id]) {
              that.navigateToItem(id);
            }
          } else {
            that.navigateToCategory(that.root.id);
          }
        }
      });
    }

    if (!this.currentCategory && this.root) {
      this.navigateToCategory(this.root.id);
    }
  }

  ngOnDestroy() {
    ( this.navigateToItemSubscription ) && this.navigateToItemSubscription.unsubscribe();
    ( this.navigateToCategorySubscription ) && this.navigateToCategorySubscription.unsubscribe();
    ( this.navigateSubscription ) && this.navigateSubscription.unsubscribe();
    ( this.catalogDataSubscription ) && this.catalogDataSubscription.unsubscribe();
    ( this.debounceSubscription ) && this.debounceSubscription.unsubscribe();
    ( this.startupInitialNavigationSubscription) && this.startupInitialNavigationSubscription.unsubscribe();
  }

  ngAfterViewInit(): void {
    // We want the catalog search to be either by pressing enter or by waiting a period of time.
    this.debounceSubscription = fromEvent(document.getElementById('catalogSearch'), 'keyup').pipe(
      debounceTime(500)
    ).subscribe(data => this.searchCatalog());
  }

  generateHighlightedItems(category: CatalogDataModel): CatalogDataModel[] {
    return this.getItemsInSubtree(category, true).filter((item) => item.highlight === true).sort((a, b) => {return a.order - b.order;}).slice(0, 3);
  }

  clickCategory(id: string) {
    this.navigateToCategory(id);
  }

  clickItem(id: string) {
    this.navigateToItem(id);
  }

  currentLevel(): number {
    if (this.searchText) {
      return -2;
    }
    return this.currentCategory ? this.currentCategory.level : -1;
  }

  // Breadcrumb navigation
  navigateTo(id: string) {
    if (id) {
      this.previousCategory = this.currentCategory;
      this.navigatedFromPortal = false;
      if (this.categoryIdToModel[id]) {
        this.navigateToCategory(id);
      } else if (this.itemIdToModel[id]) {
        this.navigateToItem(id);
      }
    }
  }

  navigateToCategory(id: string) {
    const node = this.categoryIdToModel[id];

    this.breadcrumb = this.getParents(node).reverse().map((node) => {
      return {type: node.type, id: node.id, name: $translateKey(node.localizedName || node.name)};
    });
    this.breadcrumb.push({type: node.type, id: node.id, name: $translateKey(node.localizedName || node.name)});

    this.currentItem = undefined;
    this.currentCategory = node;

    const subTreeArray = this.getItemsInSubtree(this.currentCategory, true).sort((a, b) => {return a.order - b.order;});
    const subTreeArrayNextLevel = this.getItemsInSubtree(this.currentCategory, false).sort((a, b) => {return a.order - b.order;});
    this.numItems = subTreeArray.length;
    this.numItemsNextLevel = subTreeArrayNextLevel.length;
    this.categories = this.currentCategory.categories;
    this.items = this.currentCategory.items;
    this.subtreeItems = subTreeArray;
    this.subtreeItemsNextLevel = subTreeArrayNextLevel;

    this.location = id == this.root.id ? CatalogLocation.Root : CatalogLocation.Category;

    this.router.navigateByUrl(`Catalog/${id}`);
  }

  navigateToItem(id: string) {

    // Store the current state so we can go back to it after submitting the form.

    const node = this.itemIdToModel[id];

    this.navigateToCategory(node.parent.id);

    this.breadcrumb.push({type: node.type, id: node.id, name: $translateKey(node.localizedName || node.name)});

    this.currentItem = node;

    this.location = CatalogLocation.Item;

    if (this.router.routerState.snapshot.url.startsWith("/CatalogForm")) {
      // A link is taking us directly to a catalog form. It might contain query parameters which we have to maintain.
      const queryParams = this.router.routerState.snapshot.root.queryParams;
      this.router.navigate(["CatalogForm", id], { queryParams: queryParams});
    } else {
      this.router.navigateByUrl(`Catalog/${id}`);
    }
  }

  determineLocation() {
    if (this.searchText) {
      this.location = CatalogLocation.Search;
    } else if (this.currentItem) {
      this.location = CatalogLocation.Item;
    } else if (this.currentCategory) {
      if (this.currentCategory.id == this.root.id) {
        this.location = CatalogLocation.Root;
      } else {
        this.location = CatalogLocation.Category;
      }
    }
  }

  async searchCatalog() {

    this.searchResults = [];

    const searchText = this.searchText;

    if (!searchText) {
      this.determineLocation();
      return;
    }

    if (!this.searchCatalogItemId) {
      const configResponse = await (window as any).WS.controller("FullTextSearch", {
          request_type: "default_search_config",
        }
      );

      this.searchCatalogItemId = configResponse.data.filter((config) => config.target_table === "CatalogItem").map(config => config.id)[0];
    }

    if (this.searchCatalogItemId) {
      // TODO: Temporary fix - it looks as though the search should search multiple catalogs, but there is a conflict between
      // The field definition for this.searchCatalogItemId (String), and what is in it (Array)
      let searchID = this.searchCatalogItemId;
      if (Array.isArray(searchID)) {
        searchID = searchID[0];
      }
      const itemResponse = await (window as any).WS.controller("FullTextSearch", {
          request_type: "search",
          text: searchText,
          search_type: searchID
        }
      );

      if (itemResponse.data) {
        this.searchResults = itemResponse.data.map((item: any) => {
          return this.itemIdToModel[item.id] ? this.itemIdToModel[item.id] : null;
        }).filter(x => x != null);
      }

      if (!itemResponse.data || itemResponse._error) {
        this.searchResults = [];
        this.alertService.errorMessage("The search failed to be executed!")
      }

      this.location = CatalogLocation.Search;
    }

    if (!this.searchCatalogItemId) {
      this.searchResults = [];
      this.alertService.errorMessage("Failed to execute search: Catalog Search is not configured")
    }
  }

  clearSearch() {
    this.searchText = "";
    this.searchResults = [];
    this.determineLocation();
  }

  onRequestCreated(event: CatalogRequestCreatedEvent) {
    this.lastRequestId = event.requestId;
    this.lastRequestLink = event.requestLink

    this.location = CatalogLocation.Success;
  }

  getIsChildWindow(): boolean {
    return (window as any).opener != null;
  }

  navigateToSubmittedRequest() {
    // If this appears to have been opened by Sofi, reset the form and close this window.
    if (this.isChildWindowDisplay && (window.opener as any)._SofiClient) {;
      (window.opener as any)._SofiClient.sendEvent("reset");

      setTimeout(() => {
        window.opener.location.href = this.getSubmittedItemHref();
        setTimeout(window.close);
      });
      return;
    } else {
      this.router.navigateByUrl(this.getSubmittedItemRouteHref());
    }
  }

  getSubmittedItemHref() {
    let _window: any = this.isChildWindowDisplay ? window.opener: window;

    let currentHash = _window.location.href;
    let base = (currentHash.indexOf("!") > -1) ? "/!#" : "/#";
    return `${base}${this.getSubmittedItemRouteHref()}`;
  }

  getSubmittedItemRouteHref() {
    if (this.lastRequestLink && this.lastRequestLink.length > 0) {
      return this.lastRequestLink.replace('#/', '/');
    }

    // This is such a hack - the whole aspect thing needs to be reviewed.
    // SER-1490 - always redirect to SelfService role - for now
    // const aspectSuffix = (this.userService.hasRole("platform_selfservice") && !this.userService.hasRole("administrator")) ? "/SelfService" : "";
    const aspectSuffix = "/SelfService";
    return `/${this.currentItem.table}/${this.lastRequestId}${aspectSuffix}`;
  }

  navigateToPreviousLocation() {
    // If this appears to have been opened by Sofi, reset the form and close this window.
    if (this.isChildWindowDisplay && (window.opener as any)._SofiClient) {;
      (window.opener as any)._SofiClient.sendEvent("reset");

      setTimeout(window.close);
      return;
    }

    // Normal navigation
    if (this.navigatedFromPortal) {
      this.router.navigate(['Portal']);
    } else {
      if (this?.previousCategory?.id) {
        this.navigateToCategory(this.previousCategory.id);
      }
    }
  }

  private getParents(node: CatalogDataModel) {
    const parents: CatalogDataModel[] = [];
    let parent = node.parent;
    while (parent) {
      parents.push(parent);
      parent = parent.parent;
    }

    return parents;
  }

  private populateLookups() {
    if (Object.getOwnPropertyNames(this.categoryIdToModel).length === 0) {
      const that = this;
      const process = (node: CatalogDataModel) => {
        that.categoryIdToModel[node.id] = node;
        node.items.forEach((item) => {
          that.itemIdToModel[item.id] = item;
        });

        node.categories.forEach((category) => {
          process(category);
        });
      };

      process(this.root);
    }
  }

  private getItemsInSubtree(category: CatalogDataModel, includeCategory: boolean): CatalogDataModel[] {

    const traverse = (acc: CatalogDataModel[], node: CatalogDataModel) => {
      if (category === node) {
        if (includeCategory) {
          acc.push(...node.items);
        }
      } else {
        acc.push(...node.items);
      }
      if (node.categories) {
        return node.categories.reduce(traverse, acc);
      } else {
        return acc;
      }
    };

    return traverse([], category);
  }
}
